// widgets.js — global dashboard widgets that aren't tied to any
// single page.
//
//   - tooltip      hover-tooltip that follows [data-tooltip="..."]
//   - connectionPill  the "daemon online / idle / offline" pill in
//                  the top bar
//
// Each widget exports a setup function that the legacy IIFE calls
// once during init. After setup, they read state via direct
// ES module imports (was: window.AC_LEGACY bridge — collapsed in
// Phase J7-c).
import { parseUTC } from "./util.js";
import { $ } from "./dom.js";
import { state } from "./state.js";
import { isLocalDaemonDown, isTeamUnreachable } from "./team-status.js";

// ACTIVE_HEARTBEAT_WINDOW_MS: a member counts as "active" when ANY signal of
// life arrived within this window.
//
// THIS MUST EXCEED THE DAEMON'S PUSH INTERVAL, and for a long time it didn't.
// The old value was 60s, justified as wide enough to "survive a couple of
// cycles of teamsync push (~5s each)" — but teamsync.DefaultPollInterval is
// 90 SECONDS, not 5. (The same phantom "~5s / ~30s" cadence appeared in the
// consumption-cache comments and was corrected in 0.68.7; it seems to predate
// the current syncer.) With a 90s push and a 60s window, a perfectly healthy
// daemon reported "daemon idle" for 30 of every 90 seconds — a third of the
// time — which is what a user hit on 2026-09-01 with the daemon demonstrably
// running. Measured at the time: freshest heartbeat 42s, next 44s, next 61s.
//
// 240s = 90s push × 2 missed cycles + headroom for fetch latency and clock
// skew. A daemon that has genuinely stopped still flips to idle within a few
// minutes, which is the honest resolution here: we cannot detect a stopped
// daemon faster than its push interval, and pretending otherwise is what
// produced the false "idle". Keep this ABOVE 2× DefaultPollInterval if that
// interval ever changes.
const ACTIVE_HEARTBEAT_WINDOW_MS = 240_000;

// ---- tooltip ----

// installTooltip wires up the dashboard-wide hover tooltip. Looks
// for `[data-tooltip]` on hover, populates the #ac-tooltip element
// with the value, and positions it above (or below, if no room
// above) the target. Idempotent guard prevents double-binding.
export function installTooltip() {
  if (window.AC_TOOLTIP_INSTALLED) return;
  window.AC_TOOLTIP_INSTALLED = true;

  const tipEl = document.getElementById("ac-tooltip");
  if (!tipEl) return;
  let tipTarget = null;

  document.addEventListener("mouseover", (e) => {
    const el = e.target.closest("[data-tooltip]");
    if (!el || !el.dataset.tooltip) {
      tipEl.classList.remove("visible");
      tipTarget = null;
      return;
    }
    tipTarget = el;
    tipEl.textContent = el.dataset.tooltip;
    tipEl.classList.add("visible");
    positionTooltip(tipEl, el);
  });

  document.addEventListener("mouseout", (e) => {
    const el = e.target.closest("[data-tooltip]");
    if (el === tipTarget) {
      tipEl.classList.remove("visible");
      tipTarget = null;
    }
  });
}

function positionTooltip(tipEl, el) {
  const r = el.getBoundingClientRect();
  const tw = tipEl.offsetWidth;
  const th = tipEl.offsetHeight;
  let top = r.top - th - 8;
  let left = r.left + r.width / 2 - tw / 2;
  // If above would go off-screen, put below.
  if (top < 4) top = r.bottom + 8;
  // Clamp horizontal.
  if (left < 4) left = 4;
  if (left + tw > window.innerWidth - 4)
    left = window.innerWidth - tw - 4;
  tipEl.style.top = top + "px";
  tipEl.style.left = left + "px";
}

// ---- connection pill ----

// updateConnectionPill paints the top-bar pill based on whether
// any team member's daemon is reachable. State sources:
//   - legacy.state.connected (top-level connection to coordinator)
//   - legacy.state.members[].is_online / last_seen (per-member
//     heartbeat freshness)
//
// Three visual states:
//   - "daemon online"  (active daemon detected)
//   - "daemon idle"    (a daemon has connected before but isn't
//                      currently online)
//   - "no daemon yet"  (no member has ever connected)
//   - "offline"        (we ourselves can't reach the coordinator)
export function updateConnectionPill() {
  const text = $("conn-text");
  const pill = $("conn-pill");
  if (!text) return;
  if (isLocalDaemonDown()) {
    text.textContent = "offline";
    if (pill) {
      pill.classList.remove("idle");
      pill.classList.add("disconnected");
    }
    return;
  }
  // The daemon serving this page is fine; only its team server is silent.
  if (isTeamUnreachable()) {
    text.textContent = "team unreachable";
    if (pill) {
      pill.classList.remove("disconnected");
      pill.classList.add("idle");
    }
    return;
  }
  if (!state.connected) {
    text.textContent = "offline";
    if (pill) pill.classList.remove("idle");
    return;
  }
  // Two independent signals for "is something pushing right now?":
  //   1. state.members[].last_seen — coarse (member-level), updated
  //      by the coordinator on push. Stale-edge bug here was the
  //      original cause.
  //   2. state.heartbeats[].last_seen — finer (per-agent), pushed
  //      every cycle. Treats "any per-agent heartbeat in window" as
  //      proof that the daemon is alive even when the coarser member
  //      flag hasn't flipped yet.
  // Either fresh signal → online. Falls back to is_online for very
  // old daemons that don't push activity_heartbeats.
  const nowMs = Date.now();
  const fresh = (iso) => {
    if (!iso) return false;
    const ms = parseUTC(iso);
    return Number.isFinite(ms) && nowMs - ms < ACTIVE_HEARTBEAT_WINDOW_MS;
  };
  const hasFreshMember = (state.members || []).some(
    (m) => m.is_online || fresh(m.last_seen),
  );
  const hasFreshHeartbeat = (state.heartbeats || []).some((h) =>
    fresh(h.last_seen),
  );
  const hasActiveDaemon = hasFreshMember || hasFreshHeartbeat;
  const hasEverConnected = (state.members || []).some((m) => m.last_seen);
  if (hasActiveDaemon) {
    text.textContent = "daemon online";
    if (pill) pill.classList.remove("idle");
  } else if (hasEverConnected) {
    text.textContent = "daemon idle";
    if (pill) pill.classList.add("idle");
  } else {
    text.textContent = "no daemon yet";
    if (pill) pill.classList.add("idle");
  }
}

